import { OwnershipApiError, OwnershipErrorCode } from '@commenda/api';
import type { FieldValues, UseFormSetError } from 'react-hook-form';

// Mirrors codes in BE src/features/ownership/ownership.errors.ts.
// Unknown codes fall through to the BE's raw message.
const FRIENDLY_BY_CODE: Partial<Record<string, (err: OwnershipApiError) => string>> = {
  [OwnershipErrorCode.POOL_EXCEEDED]: (err) => {
    const headroom = err.details?.headroomPercent;
    const hasOthers = err.details?.hasOtherShareholders === true;
    const headroomTxt =
      typeof headroom === 'number' ? `${headroom.toFixed(2)}%` : '0%';
    const suffix = hasOthers
      ? ' Reduce another shareholder to free up space.'
      : '';
    return `Ownership would exceed 100% — only ${headroomTxt} is available.${suffix}`;
  },
  [OwnershipErrorCode.ADD_ZERO_PERCENT_REQUIRES_HAS_SIGNIFICANT_CONTROL]: () =>
    'Set ownership above 0%, or check "Has significant control" to add as a control-only UBO.',
  [OwnershipErrorCode.EDIT_PERCENT_ZERO_UNSUPPORTED]: () =>
    'Set ownership above 0%, or delete and re-add as a UBO without ownership.',
  [OwnershipErrorCode.OWNERSHIP_CYCLE]: () =>
    'This link would create an ownership cycle. Pick a different source entity.',
  [OwnershipErrorCode.SELF_SHAREHOLDING]: () =>
    'A corporation can’t be a shareholder in itself.',
  [OwnershipErrorCode.SOURCE_CORP_DIFFERENT_WORKSPACE]: () =>
    'The source entity belongs to a different workspace.',
  [OwnershipErrorCode.HSC_REQUIRES_CONTROL_TYPE]: () =>
    'Pick a control type — it’s required when "Has significant control" is on.',
  [OwnershipErrorCode.CONTROL_TYPE_REQUIRES_HSC]: () =>
    'Control type only applies when "Has significant control" is on.',
  [OwnershipErrorCode.INHERITED_UBOS_REQUIRES_HSC]: () =>
    'Inherited UBOs can only be set when "Has significant control" is on.',
  [OwnershipErrorCode.DIRECTOR_MISSING_NATIONALITY]: () =>
    'This person doesn’t have a nationality on file. Set their citizenship from the Person profile, then try again.',
  [OwnershipErrorCode.EMPTY_PATCH]: () => 'No changes to save.',
};

export const friendlyOwnershipMessage = (err: unknown): string => {
  if (err instanceof OwnershipApiError) {
    const fn = err.code ? FRIENDLY_BY_CODE[err.code] : undefined;
    return fn ? fn(err) : err.message;
  }
  return err instanceof Error && err.message ? err.message : 'Something went wrong.';
};

// Returns true when handled inline (BE supplied a `field`); otherwise
// dispatches via `onError` and returns false.
export function applyOwnershipApiError<TForm extends FieldValues>(
  err: unknown,
  setError: UseFormSetError<TForm>,
  onError: ((message: string) => void) | undefined,
): boolean {
  if (err instanceof OwnershipApiError && err.field) {
    setError(err.field as never, {
      type: 'server',
      message: friendlyOwnershipMessage(err),
    });
    return true;
  }
  onError?.(friendlyOwnershipMessage(err));
  return false;
}
